/*
 * component-sticky-cta.js
 * Mobile-only sticky Add to cart + Buy now bar for product pages.
 *
 * Behavior:
 *  - Visible by default once the user scrolls past the real
 *    add-to-cart button (#product-add-to-cart).
 *  - Hidden when the real button is on screen (IntersectionObserver).
 *  - The sticky "Add to cart" delegates to the real form's submit by
 *    triggering a click on the real #product-add-to-cart, so all the
 *    existing variant validation, AJAX, error handling, and cart drawer
 *    opening continue to work as-is.
 *  - The sticky "Buy now" adds the currently selected variant to the
 *    cart, then redirects to /checkout. If the variant lookup fails,
 *    it falls back to navigating to the product URL with an anchor so
 *    the user can complete checkout on the page itself.
 */
(function () {
  if (typeof window === 'undefined') return;

  // Bail on desktop. The bar is hidden via CSS anyway, but we don't
  // need to spin up an observer or wire up listeners there.
  function isMobileViewport() {
    return window.matchMedia && window.matchMedia('(max-width: 1023.98px)').matches;
  }

  function init() {
    var bar = document.getElementById('ProductStickyCta');
    if (!bar) return;

    var realAddBtn = document.getElementById('product-add-to-cart');
    if (!realAddBtn) {
      // No real add-to-cart on the page (gift card, no variant, etc).
      bar.style.display = 'none';
      return;
    }

    var addBtn = bar.querySelector('.js-sticky-add-to-cart');
    var buyBtn = bar.querySelector('.js-sticky-buy-now');

    // ---- Sticky "Add to cart" delegates to the real button ----
    if (addBtn) {
      addBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (addBtn.disabled || addBtn.getAttribute('aria-disabled') === 'true') return;
        // Programmatic click on the real button: keeps the
        // <product-form> submit handler, variant validation, and the
        // cart drawer open behavior all working.
        realAddBtn.click();
      });
    }

    // ---- Sticky "Buy now": add to cart then redirect to checkout ----
    if (buyBtn) {
      buyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (buyBtn.disabled || buyBtn.getAttribute('aria-disabled') === 'true') return;

        // Find the variant id from the product form. Theme stores it in
        // a hidden input name="id" inside the product form.
        var form = realAddBtn.closest('form');
        var variantInput = form ? form.querySelector('input[name="id"]') : null;
        var variantId = variantInput ? variantInput.value : null;
        if (!variantId) {
          // Fallback: just click the real add (which surfaces any
          // missing-variant errors), then we cannot reliably redirect.
          realAddBtn.click();
          return;
        }

        // Loading state
        buyBtn.disabled = true;
        var originalLabel = buyBtn.innerHTML;
        buyBtn.innerHTML = '<span>...</span>';

        var cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

        fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
          .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
          .then(function (result) {
            if (!result.ok) {
              // Bubble error through the existing form: trigger real button.
              buyBtn.disabled = false;
              buyBtn.innerHTML = originalLabel;
              realAddBtn.click();
              return;
            }
            window.location.href = '/checkout';
          })
          .catch(function () {
            buyBtn.disabled = false;
            buyBtn.innerHTML = originalLabel;
            // Fall back to triggering the real add button so the user
            // still gets a clear error message in the existing UI.
            realAddBtn.click();
          });
      });
    }

    // ---- Visibility: hide bar when real button is on screen ----
    if (!('IntersectionObserver' in window)) {
      // Old browsers: just always show.
      bar.classList.remove('is-hidden');
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // We only show the bar when the user has scrolled past the
        // real add-to-cart button (so it's no longer visible).
        if (entry.isIntersecting) {
          bar.classList.add('is-hidden');
        } else {
          if (isMobileViewport()) {
            bar.classList.remove('is-hidden');
          }
        }
      });
    }, {
      // Slightly inset rootMargin so the bar doesn't flicker right at
      // the threshold: it only shows once the real button is clearly
      // off screen.
      root: null,
      rootMargin: '0px 0px -20px 0px',
      threshold: 0
    });

    observer.observe(realAddBtn);

    // ---- Keep variant availability in sync ----
    // When the user switches a sold-out variant (or back to an available
    // one), the real #product-add-to-cart's disabled / class state is
    // updated by <product-form> / variant-radios. We mirror that on the
    // sticky buttons so they reflect the same availability.
    function syncAvailability() {
      var isUnavailable =
        realAddBtn.disabled ||
        realAddBtn.classList.contains('product-form__create-alert-btn') ||
        realAddBtn.getAttribute('type') === 'button'; // theme sets type=button when unavailable
      if (isUnavailable) {
        if (addBtn) { addBtn.disabled = true; addBtn.setAttribute('aria-disabled', 'true'); }
        if (buyBtn) { buyBtn.disabled = true; buyBtn.setAttribute('aria-disabled', 'true'); }
      } else {
        if (addBtn) { addBtn.disabled = false; addBtn.removeAttribute('aria-disabled'); }
        if (buyBtn) { buyBtn.disabled = false; buyBtn.removeAttribute('aria-disabled'); }
      }
    }
    syncAvailability();

    var attrObserver = new MutationObserver(syncAvailability);
    attrObserver.observe(realAddBtn, {
      attributes: true,
      attributeFilter: ['disabled', 'class', 'type']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
